import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchGame } from '../../../api/GamesAPI';
import { scoreDelivery, updateDelivery } from '../../../api/ScoringAPI';
import './LiveScoringPage.css';

// Import the new child components
import ScoringSetup from './ScoringSetup';
import PlayerDisplay from './PlayerDisplay';
import OverSummary from './OverSummary';
import ScoringControls from './ScoringControls';
import EditDeliveryModal from './EditDeliveryModal';
import ChangePlayerModal from './ChangePlayerModal';
import WicketModal from './WicketModal';
import ScoringModal from './ScoringModal';


const LiveScoringPage = () => {
    const { id: gameId } = useParams();
    const navigate = useNavigate();
    const [game, setGame] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Core scoring state
    const [isSetupComplete, setIsSetupComplete] = useState(false);
    const [striker, setStriker] = useState(null);
    const [nonStriker, setNonStriker] = useState(null);
    const [bowler, setBowler] = useState(null);
    const [totalScore, setTotalScore] = useState(0);
    const [totalWickets, setTotalWickets] = useState(0);
    const [totalBalls, setTotalBalls] = useState(0);
    const [thisOver, setThisOver] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Modal states
    const [showScoringModal, setShowScoringModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [showWicketModal, setShowWicketModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingDelivery, setEditingDelivery] = useState(null);
    const [showChangePlayerModal, setShowChangePlayerModal] = useState(false);
    const [playerToChange, setPlayerToChange] = useState(null);

    // Fetch game data on component mount
    useEffect(() => {
        fetchGame(gameId, (response) => {
            if (response.game) {
                setGame(response.game);
                // In a real app, you would initialize the score here if resuming a game
            } else {
                navigate('/club_portal/games');
            }
            setIsLoading(false);
        });
    }, [gameId, navigate]);

    const handleSetupComplete = (setup) => {
        setStriker(setup.striker);
        setNonStriker(setup.nonStriker);
        setBowler(setup.bowler);
        setIsSetupComplete(true);
    };

    const openScoringModal = (baseData) => {
        setModalData(baseData);
        setShowScoringModal(true);
    };

    const handleScoreDelivery = (deliveryData) => {
        setIsSubmitting(true);
        const currentOver = Math.floor(totalBalls / 6);
        const currentBallInOver = (totalBalls % 6) + 1;

        const finalDeliveryData = {
            ...deliveryData,
            batting_team_id: striker.game_team_id,
            bowling_team_id: bowler.game_team_id,
            inning_number: 1, // Simplified for now
            over_number: currentOver,
            ball_number: currentBallInOver,
            batsman_id: striker.id,
            bowler_id: bowler.id,
        };

        scoreDelivery(gameId, finalDeliveryData, (response) => {
            setIsSubmitting(false);
            setShowScoringModal(false);
            setShowWicketModal(false);

            if (response.success) {
                // Update local state for immediate feedback
                setThisOver(prev => [...prev, { ...finalDeliveryData, temp_id: Date.now() }]);
                const runsForScore = finalDeliveryData.runs_scored + finalDeliveryData.extra_runs;
                setTotalScore(prev => prev + runsForScore);

                if (finalDeliveryData.is_wicket) {
                    setTotalWickets(prev => prev + 1);
                }

                const isLegalDelivery = !['wide', 'noball'].includes(finalDeliveryData.extra_type);
                if (isLegalDelivery) {
                    const newTotalBalls = totalBalls + 1;
                    setTotalBalls(newTotalBalls);
                    // End of over logic
                    if (newTotalBalls % 6 === 0) {
                        setThisOver([]);
                        const temp = striker; setStriker(nonStriker); setNonStriker(temp);
                        // Open modal to select next bowler
                    }
                }
                
                // Swap striker on odd runs (mid-over)
                if (finalDeliveryData.runs_scored % 2 !== 0 && (totalBalls + 1) % 6 !== 0) {
                    const temp = striker; setStriker(nonStriker); setNonStriker(temp);
                }

            } else {
                alert("Failed to record delivery.");
            }
        });
    };

    const handleUpdateDelivery = (updatedDelivery) => {
        updateDelivery(updatedDelivery, (response) => {
            if (response.success) {
                setShowEditModal(false);
                alert("Delivery updated! A full page refresh is recommended to see stat changes.");
            } else {
                alert("Failed to update delivery.");
            }
        });
    };
    
    const handleChangePlayer = (player) => {
        if (playerToChange === 'Striker') setStriker(player);
        if (playerToChange === 'Non-Striker') setNonStriker(player);
        if (playerToChange === 'Bowler') setBowler(player);
        setShowChangePlayerModal(false);
    };

    if (isLoading) return <div className="loading-container"><div className="spinner-border"></div></div>;
    if (!game) return <div className="loading-container">Game not found.</div>;
    
    const overs = `${Math.floor(totalBalls / 6)}.${totalBalls % 6}`;
    const battingTeam = game.teams.find(t => t.id === striker?.game_team_id);
    const bowlingTeam = game.teams.find(t => t.id === bowler?.game_team_id);

    return (
        <div className="scoring-page">
            {!isSetupComplete ? (
                <ScoringSetup game={game} onSetupComplete={handleSetupComplete} />
            ) : (
                <>
                    <header className="scoring-header">
                        <h3>{battingTeam?.name} vs {bowlingTeam?.name}</h3>
                        <div><h4>{totalScore} / {totalWickets}</h4><p>Overs: {overs}</p></div>
                    </header>
                    <main className="scoring-main">
                        <PlayerDisplay
                            striker={striker}
                            nonStriker={nonStriker}
                            bowler={bowler}
                            onChangeClick={(type) => { setPlayerToChange(type); setShowChangePlayerModal(true); }}
                        />
                        <OverSummary
                            thisOver={thisOver}
                            onEditClick={(delivery) => { setEditingDelivery(delivery); setShowEditModal(true); }}
                        />
                    </main>
                    <ScoringControls
                        onModalOpen={openScoringModal}
                        onWicketClick={() => setShowWicketModal(true)}
                        isSubmitting={isSubmitting}
                    />
                    
                    <ScoringModal
                        show={showScoringModal}
                        onHide={() => setShowScoringModal(false)}
                        baseData={modalData}
                        onScore={handleScoreDelivery}
                    />

                    <WicketModal
                        show={showWicketModal}
                        onHide={() => setShowWicketModal(false)}
                        onConfirm={handleScoreDelivery}
                        battingTeamPlayers={battingTeam?.players || []}
                        fieldingTeamPlayers={bowlingTeam?.players || []}
                        outBatsman={striker}
                    />
                    
                    <EditDeliveryModal
                        show={showEditModal}
                        onHide={() => setShowEditModal(false)}
                        delivery={editingDelivery}
                        onUpdate={handleUpdateDelivery}
                    />
                    
                    <ChangePlayerModal
                        show={showChangePlayerModal}
                        onHide={() => setShowChangePlayerModal(false)}
                        playerType={playerToChange}
                        players={playerToChange === 'Bowler' ? (bowlingTeam?.players || []) : (battingTeam?.players || [])}
                        onPlayerSelect={handleChangePlayer}
                    />
                </>
            )}
        </div>
    );
};

export default LiveScoringPage;

